import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import plannerLogo from "../assets/logo/Logo-Planner-Turni.png";
import { api } from "../utils/api";
import PublicTalkPodiumIcon from "./icons/PublicTalkPodiumIcon";
import {
  HomeIcon,
  MapPinIcon,
  UsersIcon,
  ClockIcon,
  CogIcon,
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  BookOpenIcon,
  CheckCircleIcon,
  FolderIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
  ArrowsRightLeftIcon,
  PresentationChartBarIcon,
} from "@heroicons/react/24/outline";
import { clsx } from "clsx";

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, activeCongregazione } = useAuth();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [canManagePulizie, setCanManagePulizie] = useState(false);
  // Inizializza isMobile controllando immediatamente la dimensione dello schermo
  // Questo evita il glitch durante il rendering iniziale
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 768; // md breakpoint di Tailwind
    }
    return false;
  });
  const previousPathRef = useRef(location.pathname);

  // Determina lo stato di apertura della sidebar
  const sidebarIsOpen = isOpen !== undefined ? isOpen : !isMobile;

  // Rileva se siamo su mobile e aggiorna lo stato
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint di Tailwind
    };
    
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Chiudi sidebar su mobile quando cambia la route
  useEffect(() => {
    const hasRouteChanged = previousPathRef.current !== location.pathname;
    if (hasRouteChanged && isMobile && sidebarIsOpen && onClose) {
      onClose();
    }
    previousPathRef.current = location.pathname;
  }, [location.pathname, isMobile, sidebarIsOpen, onClose]);

  const toggleExpanded = (itemKey) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemKey)) {
      newExpanded.delete(itemKey);
    } else {
      newExpanded.add(itemKey);
    }
    setExpandedItems(newExpanded);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isSuperAdmin = user?.ruolo === "super_admin";
  const currentCongregazione =
    activeCongregazione ||
    (user?.congregazione_id
      ? {
          id: user.congregazione_id,
          codice: user.congregazione_codice,
          nome: user.congregazione_nome,
          servizio_speciale: user.congregazione_servizio_speciale,
          gruppi_servizio_visibili:
            user.congregazione_gruppi_servizio_visibili ?? false,
        }
      : null);

  const isServizioSpeciale = Boolean(currentCongregazione?.servizio_speciale);
  const isAdminOrSuper = user?.ruolo === "admin" || isSuperAdmin;
  const publicTalkCoordinatorCongregationIds = Array.isArray(
    user?.public_talk_coordinator_congregazione_ids
  )
    ? user.public_talk_coordinator_congregazione_ids.map(Number)
    : [];
  const canManagePublicTalks =
    isAdminOrSuper ||
    (currentCongregazione?.id &&
      publicTalkCoordinatorCongregationIds.includes(
        Number(currentCongregazione.id)
      ));
  const isServiceLeader = user?.service_leader && user?.sesso === "M";
  const canViewGruppiServizio =
    !isServizioSpeciale &&
    (isAdminOrSuper || Boolean(currentCongregazione?.gruppi_servizio_visibili));
  const useLegacyDisponibilitaMenu =
    !currentCongregazione?.id || isServizioSpeciale;

  // Verifica se l'utente (non admin) può gestire i turni pulizie tramite permesso delegato
  useEffect(() => {
    let cancelled = false;
    const checkPulizie = async () => {
      if (isAdminOrSuper || !currentCongregazione?.id || !user?.id) {
        setCanManagePulizie(isAdminOrSuper);
        return;
      }
      try {
        const res = await api.get("/turni-pulizie/permessi");
        if (cancelled) return;
        const permessi = res.data?.volontari || [];
        setCanManagePulizie(permessi.includes(user.id));
      } catch (_err) {
        if (!cancelled) {
          setCanManagePulizie(false);
        }
      }
    };
    checkPulizie();
    return () => {
      cancelled = true;
    };
  }, [isAdminOrSuper, currentCongregazione?.id, user?.id]);

  const postazioniSubItems = [
    { label: "Elenco Postazioni", path: "/postazioni" },
    {
      label: "Gestione Postazioni",
      path: "/postazioni/gestione",
      adminOnly: true,
    },
  ];

  if (!useLegacyDisponibilitaMenu) {
    postazioniSubItems.push({
      label: "Disponibilità Postazioni",
      path: "/volontari/disponibilita",
    });
  }

  const rawMenuItems = [
    {
      key: "dashboard",
      label: "Bacheca",
      icon: HomeIcon,
      path: "/",
      adminOnly: false,
    },
    {
      key: "miei-turni",
      label: "I miei turni",
      icon: CalendarDaysIcon,
      path: "/miei-turni",
      adminOnly: false,
    },
  ];

  if (useLegacyDisponibilitaMenu) {
    rawMenuItems.push({
      key: "disponibilita",
      label: "Disponibilità",
      icon: CheckCircleIcon,
      path: "/volontari/disponibilita",
      adminOnly: false,
    });
  }

  rawMenuItems.push({
    key: "postazioni",
    label: "Postazioni",
    icon: MapPinIcon,
    path: "/postazioni",
    adminOnly: false,
    subItems: postazioniSubItems,
  });

  if (!isServizioSpeciale) {
    rawMenuItems.push({
      key: "adunanze",
      label: "Adunanze settimanali",
      icon: CalendarDaysIcon,
      path: "/adunanze",
      adminOnly: false,
    });
  }

  const adunanzeServizioSubItems = [
    {
      label: "Luoghi e orari Adunanze",
      path: "/adunanze-servizio",
      adminOnly: false,
    },
  ];
  const showDisponibilitaAS = !useLegacyDisponibilitaMenu && (isServiceLeader || isAdminOrSuper);

  if (showDisponibilitaAS) {
    adunanzeServizioSubItems.push({
      label: "Disponibilità conduttori",
      path: "/adunanze-servizio/disponibilita",
      adminOnly: false,
    });
  }
  if (!isServizioSpeciale && adunanzeServizioSubItems.length > 0) {
    rawMenuItems.push({
      key: "adunanze-servizio",
      label: "Adunanze Servizio",
      icon: PresentationChartBarIcon,
      path: adunanzeServizioSubItems[0].path,
      adminOnly: false,
      subItems: adunanzeServizioSubItems,
    });
  }

  const turniSubItems = useLegacyDisponibilitaMenu
    ? [
        { label: "Gestione Turni", path: "/turni/gestione" },
        { label: "Turni Incompleti", path: "/turni/incompleti" },
        ...(isServizioSpeciale ? [] : [{ label: "Turni Pulizie", path: "/turni/pulizie" }]),
      ]
    : [
        { label: "Turni Postazioni", path: "/turni/gestione" },
        {
          label: "Turni Conduttori A.S.",
          path: "/turni/conduttori",
          adminOnly: true,
        },
        ...(isServizioSpeciale ? [] : [{ label: "Turni Pulizie", path: "/turni/pulizie" }]),
      ];
  const turniMenuItem = {
    key: "turni",
    label: "Gestione Turni",
    icon: ClipboardDocumentListIcon,
    path: turniSubItems[0].path,
    adminOnly: true,
    subItems: turniSubItems,
  };

  const registroSubItems = [
    { label: "Turni Passati", path: "/registro/turni-passati" },
    {
      label: "Statistiche",
      path: "/registro/statistiche",
      adminOnly: true,
    },
    !isServizioSpeciale
      ? {
          label: "Registro Servizio",
          path: "/registro/servizio",
          adminOnly: false,
        }
      : null,
    {
      label: "Eventi",
      path: "/registro/eventi",
      adminOnly: false,
    },
  ].filter(Boolean);

  rawMenuItems.push(
    {
      key: "volontari",
      label: "Proclamatori",
      icon: UsersIcon,
      path: "/volontari",
      adminOnly: false,
      subItems: [
        { label: "Elenco Volontari", path: "/volontari" },
        {
          label: "Gestione Volontari",
          path: "/volontari/gestione",
          adminOnly: true,
        },
        {
          label: "Gruppi di Servizio",
          path: "/volontari/gruppi-servizio",
          adminOnly: false,
        },
        {
          label: "Riepilogo Disponibilità",
          path: "/volontari/riepilogo-disponibilita",
          adminOnly: true,
        },
        {
          label: "Rapporti di Servizio",
          path: "/volontari/rapporti-servizio",
          adminOnly: true,
        },
        {
          label: "Deleghe",
          path: "/volontari/deleghe",
          adminOnly: true,
        },
      ],
    },
    {
      key: "discorsi-pubblici",
      label: "Discorsi Pubblici",
      icon: PublicTalkPodiumIcon,
      path: "/discorsi-pubblici/entrata",
      adminOnly: false,
      hidden: isServizioSpeciale || !canManagePublicTalks,
      subItems: [
        { label: "Oratori in entrata", path: "/discorsi-pubblici/entrata" },
        { label: "Oratori in uscita", path: "/discorsi-pubblici/uscita" },
        { label: "Calendario", path: "/discorsi-pubblici/calendario" },
        { label: "Approvazioni", path: "/discorsi-pubblici/approvazioni" },
        { label: "Catalogo schemi", path: "/discorsi-pubblici/schemi" },
        { label: "Impostazioni", path: "/discorsi-pubblici/impostazioni" },
      ],
    },
    {
      key: "registro",
      label: "Registro",
      icon: ClockIcon,
      path: "/registro/turni-passati",
      adminOnly: false,
      subItems: registroSubItems,
    },
    !isAdminOrSuper && canManagePulizie
      ? {
          key: "turni-pulizie",
          label: "Turni Pulizie",
          icon: CheckCircleIcon,
          path: "/turni/pulizie",
          adminOnly: false,
        }
      : null,
    {
      key: "esperienze",
      label: "Esperienze",
      icon: BookOpenIcon,
      path: "/esperienze",
      adminOnly: false,
    },
    {
      key: "documenti",
      label: "Documenti",
      icon: FolderIcon,
      path: "/documenti",
      adminOnly: false,
      subItems: [
        {
          label: "Autorizzazioni",
          path: "/documenti/autorizzazioni",
          adminOnly: false,
        },
        {
          label: "Istruzioni",
          path: "/documenti/istruzioni",
          adminOnly: false,
        },
        {
          label: "Contatto Responsabile",
          path: "/documenti/contatto-responsabile",
          adminOnly: false,
        },
      ],
    },
    {
      key: "impostazioni",
      label: "Impostazioni",
      icon: CogIcon,
      path: "/impostazioni",
      adminOnly: false,
      subItems: [
        { label: "Configurazione", path: "/impostazioni", adminOnly: false },
        { label: "Notifiche", path: "/notifiche", adminOnly: false },
        { label: "Assistenza", path: "/assistenza", adminOnly: false },
      ],
    },
    {
      key: "scegli-servizio",
      label: "Scegli Servizio",
      icon: ArrowsRightLeftIcon,
      path: "/scegli-servizio",
      adminOnly: false,
    },
    {
      key: "congregazioni",
      label: "Tenant Manager",
      icon: UserCircleIcon,
      path: "/congregazioni",
      superAdminOnly: true,
    }
  );

  if (useLegacyDisponibilitaMenu) {
    const disponibilitaIndex = rawMenuItems.findIndex(
      (item) => item?.key === "disponibilita"
    );
    if (disponibilitaIndex !== -1) {
      rawMenuItems.splice(disponibilitaIndex + 1, 0, turniMenuItem);
    } else {
      rawMenuItems.push(turniMenuItem);
    }
  } else {
    const registroIndex = rawMenuItems.findIndex(
      (item) => item?.key === "registro"
    );
    if (registroIndex !== -1) {
      rawMenuItems.splice(registroIndex + 1, 0, turniMenuItem);
    } else {
      rawMenuItems.push(turniMenuItem);
    }
  }

  const menuItems = rawMenuItems.filter((item) => item && !item.hidden);

  // Espandi automaticamente i menu che contengono la pagina corrente
  useEffect(() => {
    const activeMenuKey = menuItems.find((item) => {
      if (!item) return false;
      if (item.path && location.pathname === item.path) return true;
      return item.subItems?.some(
        (subItem) => subItem?.path && location.pathname === subItem.path
      );
    })?.key;

    if (activeMenuKey) {
      setExpandedItems(prev => {
        if (!prev.has(activeMenuKey)) {
          return new Set(prev).add(activeMenuKey);
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const renderMenuItem = (item) => {
    if (item.superAdminOnly && !isSuperAdmin) {
      return null;
    }

    if (item.adminOnly && !(user?.ruolo === "admin" || isSuperAdmin)) {
      return null;
    }

    const Icon = item.icon;
    const isExpanded = expandedItems.has(item.key);
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isHighlighted = item.key === "congregazioni";
    
    // Espandi automaticamente se siamo su una pagina figlia
    const shouldBeExpanded = hasSubItems && (
      isExpanded || 
      item.subItems?.some(subItem => isActive(subItem.path))
    );

    return (
      <div key={item.key}>
        <Link
          to={hasSubItems && !shouldBeExpanded ? item.subItems[0].path : (hasSubItems ? "#" : item.path)}
          onClick={
            hasSubItems
              ? (e) => {
                  e.preventDefault();
                  toggleExpanded(item.key);
                }
              : handleLinkClick
          }
          className={clsx(
            "flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors duration-200",
            (isActive(item.path) || shouldBeExpanded) && !hasSubItems
              ? "bg-primary-100 text-primary-700 border-r-2 border-primary-600"
              : shouldBeExpanded
              ? "bg-gray-50 text-gray-900"
              : isHighlighted
              ? "bg-blue-50/70 text-blue-700 hover:bg-blue-100/70"
              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          )}
        >
          <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{item.label}</span>
          {hasSubItems &&
            (shouldBeExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            ))}
        </Link>

        {hasSubItems && shouldBeExpanded && (
          <div className="ml-8 mt-1 space-y-1">
            {item.subItems.map((subItem) => {
              // Controlla se l'utente ha i permessi per vedere questo sub-elemento
              if (
                subItem.superAdminOnly &&
                !isSuperAdmin
              ) {
                return null;
              }

              if (
                subItem.path === "/volontari/gruppi-servizio" &&
                !canViewGruppiServizio
              ) {
                return null;
              }

              if (subItem.adminOnly && !(user?.ruolo === "admin" || isSuperAdmin)) {
                return null;
              }

              return (
                <Link
                  key={subItem.path}
                  to={subItem.path}
                  onClick={handleLinkClick}
                  className={clsx(
                    "block px-4 py-2 text-sm rounded-md transition-colors duration-200",
                    isActive(subItem.path)
                      ? "bg-primary-50 text-primary-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  {subItem.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Gestisci click su link per chiudere sidebar su mobile
  const handleLinkClick = () => {
    if (isMobile && sidebarIsOpen && onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Overlay su mobile quando sidebar è aperto */}
      {isMobile && sidebarIsOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      
      <div
        className={clsx(
          "flex flex-col w-64 bg-white shadow-lg",
          "fixed md:static inset-y-0 left-0 z-50",
          "transform transition-transform duration-300 ease-in-out",
          // Su mobile: nascosto se chiuso, visibile se aperto
          isMobile && !sidebarIsOpen && "-translate-x-full",
          isMobile && sidebarIsOpen && "translate-x-0",
          // Su desktop: sempre visibile (rimuove translate)
          "md:translate-x-0 md:transform-none"
        )}
        style={{
          // Assicura che su mobile la sidebar parta nascosta
          ...(isMobile && !sidebarIsOpen ? { transform: 'translateX(-100%)' } : {}),
        }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 relative">
          {/* Bottone chiudi su mobile */}
          {isMobile && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors z-10"
              aria-label="Chiudi menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <img
                src={plannerLogo}
                alt="Planner Turni logo"
                className="h-10 w-10 object-contain rounded"
              />
              <h1 className="ml-3 text-lg font-semibold text-gray-900">
                Planner Turni
              </h1>
            </div>
          </div>
          
          <div className="space-y-2">
            {currentCongregazione?.id ? (
              <div className="px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-[11px] font-semibold text-primary-600 uppercase tracking-wide">
                  Congregazione attiva
                </p>
                <p className="text-sm font-medium text-gray-900 truncate">
                  {currentCongregazione.nome}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>Codice: {currentCongregazione.codice}</span>
                  {currentCongregazione.servizio_speciale && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-800">
                      Servizio Speciale
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs font-medium text-yellow-700">
                  Nessuna congregazione selezionata. Usa “Scegli Servizio”.
                </p>
              </div>
            )}

            {currentCongregazione?.servizio_speciale && (
              <div className="px-3 py-2 bg-purple-50/80 border border-purple-200 rounded-lg flex items-start text-xs text-purple-800 space-x-2">
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-purple-600" />
                <p>
                  Modalità Servizio Speciale attiva: l'autocompilazione usa esclusivamente
                  i delegati e richiede disponibilità registrate nella congregazione speciale.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {menuItems.map(renderMenuItem)}
        </nav>

        {/* User Profile Section */}
        <div className="border-t border-gray-200 px-4 py-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserCircleIcon className="h-8 w-8 text-gray-400" />
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.nome} {user?.cognome}
              </p>
              <p className="text-xs text-gray-500 truncate capitalize">
                {user?.ruolo || "volontario"}
              </p>
            </div>
            <button
              onClick={() => {
                logout();
                if (isMobile && onClose) {
                  onClose();
                }
              }}
              className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              title="Logout"
            >
              <ArrowLeftOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
